import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faBox,
  faDollarSign,
  faClock,
  faMapMarkerAlt,
  faEnvelope,
  faTruck,
  faPlus,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import { CircularProgress } from "@mui/material";
import Card from "../ui/Card";
import ActionButton from "../ui/ActionButton";
import { useProfileData } from "../../hooks/useProfileData";
import { useMaterialTypes } from "../../hooks/useMaterialTypes";
import MaterialSelect from "./MaterialSelect";
import "../../style/DetailedViewPanel.css";

const INITIAL_FORM_STATE = {
  material: "",
  item_name: "",
  quantity: "",
  cost: "",
  acquisition_cost: "",
  expiry_disposition: "",
  latest_pickup: "",

  delivery_method: "pickup", // "pickup" | "delivery"
  transport_mode: "",

  delivery_instructions: "",
  contact_email: "",
  contact_phone: "",
  address: "",
  city: "",
  share_contact: true,
  use_default_address: true,
  pickup_time_start: "09:00",
  pickup_time_end: "17:00",
  available_days: {
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
  },
};

const AddListingPanel = ({
  darkMode,
  onClose,
  onSubmit,
  isSubmitting = false,
  listingsData,
  initialData,
}) => {
  const { t } = useTranslation();
  const [isClosing, setIsClosing] = useState(false);
  const isDuplicate = !!initialData;
  const [formData, setFormData] = useState(
    initialData
      ? { ...INITIAL_FORM_STATE, ...initialData, latest_pickup: "" }
      : INITIAL_FORM_STATE
  );
  const [errors, setErrors] = useState({});

  const { profile, loading: profileLoading } = useProfileData();
  const { materialTypes, loading: materialTypesLoading } = useMaterialTypes();

  useEffect(() => {
    if (isDuplicate || !profile || profileLoading) return;
    setFormData((prev) => ({
      ...prev,
      contact_email: profile.email || "",
      contact_phone: profile.phone_number || "",
      address: profile.address || "",
      city: profile.city || "",
    }));
  }, [profile, profileLoading, isDuplicate]);

  useEffect(() => {
    setErrors({});
  }, []);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleDayToggle = (day) => {
    setFormData((prev) => ({
      ...prev,
      available_days: {
        ...prev.available_days,
        [day]: !prev.available_days[day],
      },
    }));
    if (errors.available_days) {
      setErrors((prev) => ({ ...prev, available_days: "" }));
    }
  };

  const validatePickupTimes = () => {
    const timeErrors = {};
    const startTime = formData.pickup_time_start;
    const endTime = formData.pickup_time_end;
    if (!startTime || !endTime) return timeErrors;

    const startMinutes =
      parseInt(startTime.split(":")[0]) * 60 + parseInt(startTime.split(":")[1]);
    const endMinutes =
      parseInt(endTime.split(":")[0]) * 60 + parseInt(endTime.split(":")[1]);

    if (startMinutes >= endMinutes) {
      timeErrors.pickup_time_start = t("Start time must be before end time");
      timeErrors.pickup_time_end = t("End time must be after start time");
    }

    const durationMinutes = endMinutes - startMinutes;
    if (durationMinutes < 60) {
      timeErrors.pickup_time_start = t("Pickup window must be at least 1 hour");
      timeErrors.pickup_time_end = t("Pickup window must be at least 1 hour");
    }

    return timeErrors;
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.material) {
      newErrors.material = t("Material type is required");
    }

    if (!formData.quantity) {
      newErrors.quantity = t("Quantity is required");
    } else if (isNaN(formData.quantity)) {
      newErrors.quantity = t("Quantity must be a valid number");
    } else if (parseFloat(formData.quantity) <= 0) {
      newErrors.quantity = t("Quantity must be greater than 0");
    } else if (parseFloat(formData.quantity) > 1000000) {
      newErrors.quantity = t("Quantity must be less than 1,000,000");
    }

    if (!formData.cost && formData.cost !== 0) {
      newErrors.cost = t("Cost is required");
    } else if (isNaN(formData.cost)) {
      newErrors.cost = t("Cost must be a valid number");
    } else if (parseFloat(formData.cost) < 0) {
      newErrors.cost = t("Cost must be 0 or greater");
    } else if (parseFloat(formData.cost) > 10000) {
      newErrors.cost = t("Cost must be less than $10,000");
    }

    if (formData.acquisition_cost !== "" && formData.acquisition_cost != null) {
      if (isNaN(formData.acquisition_cost)) {
        newErrors.acquisition_cost = t("Acquisition cost must be a valid number");
      } else if (parseFloat(formData.acquisition_cost) < 0) {
        newErrors.acquisition_cost = t("Acquisition cost must be 0 or greater");
      } else if (parseFloat(formData.acquisition_cost) > 1000000) {
        newErrors.acquisition_cost = t("Acquisition cost must be less than $1,000,000");
      }
    }

    if (!formData.latest_pickup) {
      newErrors.latest_pickup = t("Pickup deadline is required");
    } else {
      const pickupDate = new Date(formData.latest_pickup);
      if (isNaN(pickupDate.getTime())) {
        newErrors.latest_pickup = t("Please enter a valid date");
      } else {
        const todayDate = new Date();
        todayDate.setHours(0, 0, 0, 0);
        if (pickupDate < todayDate) {
          newErrors.latest_pickup = t("Pickup deadline cannot be in the past");
        }
        const oneYearFromNow = new Date();
        oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
        if (pickupDate > oneYearFromNow) {
          newErrors.latest_pickup = t("Pickup deadline must be within 1 year");
        }
      }
    }

    if (
      (formData.delivery_method || "pickup") === "delivery" &&
      (!formData.transport_mode || !formData.transport_mode.trim())
    ) {
      newErrors.transport_mode = t("Mode of transportation is required");
    }

    const timeErrors = validatePickupTimes();
    Object.assign(newErrors, timeErrors);

    const hasSelectedDay = Object.values(formData.available_days).some((day) => day);
    if (!hasSelectedDay) {
      newErrors.available_days = t("Please select at least one available day");
    }

    if (formData.share_contact) {
      if (!formData.contact_email) {
        newErrors.contact_email = t("Contact email is required");
      } else if (!/\S+@\S+\.\S+/.test(formData.contact_email)) {
        newErrors.contact_email = t("Please enter a valid email address");
      }
    }

    if (!formData.contact_phone || !formData.contact_phone.trim()) {
      newErrors.contact_phone = t("Contact phone is required");
    }

    if (!formData.use_default_address) {
      if (!formData.address || !formData.address.trim()) {
        newErrors.address = t("Address is required");
      }
      if (!formData.city || !formData.city.trim()) {
        newErrors.city = t("City is required");
      }
    }

    if (formData.delivery_instructions && formData.delivery_instructions.length > 500) {
      newErrors.delivery_instructions = t(
        "Delivery instructions must be less than 500 characters"
      );
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const customEmail =
      formData.contact_email !== profile?.email ? formData.contact_email.trim() : null;
    const customPhone =
      formData.contact_phone !== profile?.phone_number
        ? formData.contact_phone.trim()
        : null;
    const customAddress =
      !formData.use_default_address && formData.address !== profile?.address
        ? formData.address.trim()
        : null;
    const customCity =
      !formData.use_default_address && formData.city !== profile?.city
        ? formData.city.trim()
        : null;

    onSubmit({
      material: formData.material,
      item_name: formData.item_name.trim() || null,
      quantity: parseFloat(formData.quantity),
      cost: parseFloat(formData.cost),
      acquisition_cost: formData.acquisition_cost !== "" ? parseFloat(formData.acquisition_cost) : null,
      expiry_disposition: formData.expiry_disposition || null,
      latest_pickup: formData.latest_pickup,

      delivery_method: formData.delivery_method || "pickup",
      transport_mode:
        (formData.delivery_method || "pickup") === "delivery"
          ? (formData.transport_mode || "").trim()
          : null,

      delivery_instructions: formData.delivery_instructions.trim(),
      custom_email: customEmail,
      custom_phone: customPhone,
      custom_address: customAddress,
      custom_city: customCity,
      share_contact: formData.share_contact,
      use_default_address: formData.use_default_address,
      pickup_time_start: formData.pickup_time_start,
      pickup_time_end: formData.pickup_time_end,
      available_days: formData.available_days,
      ...(initialData?.duplicated_from && { duplicated_from: initialData.duplicated_from }),
    });
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => onClose(), 300);
  };

  const today = new Date().toISOString().split("T")[0];

  const daysConfig = [
    { key: "monday", label: "M" },
    { key: "tuesday", label: "T" },
    { key: "wednesday", label: "W" },
    { key: "thursday", label: "Th" },
    { key: "friday", label: "F" },
    { key: "saturday", label: "S" },
    { key: "sunday", label: "Su" },
  ];

  if (profileLoading || materialTypesLoading) {
    return (
      <Card variant="system" darkMode={darkMode} className="detailed-view-panel">
        <div className="panel-loading">
          <CircularProgress sx={{ color: "var(--role-accent)" }} />
          <span>{t("Loading...")}</span>
        </div>
      </Card>
    );
  }

  return (
    <Card
      variant="system"
      darkMode={darkMode}
      className={`detailed-view-panel add-listing-panel ${isClosing ? "closing" : ""}`}
    >
      {/* Header */}
      <div className="panel-header">
        <div className="panel-header-left">
          <h3 className="panel-title">
            <span className="panel-title-icon">
              <FontAwesomeIcon icon={faPlus} />
            </span>
            {isDuplicate ? t("Duplicate Listing") : t("Add New Listing")}
            <span className="panel-header-separator" />
            <span className="panel-subtitle">
              {isDuplicate
                ? t("Pre-filled from existing listing — adjust as needed")
                : t("Create a new material donation listing")}
            </span>
          </h3>
        </div>
        <button
          onClick={handleClose}
          className="close-button"
          aria-label="Close"
          disabled={isSubmitting}
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="panel-content-grid">
        {/* LEFT COLUMN */}
        <div className="panel-column">
          {/* Material & Quantity Card */}
          <div className="info-card">
            <div className="info-card-header">
              <div className="info-card-icon">
                <FontAwesomeIcon icon={faBox} />
              </div>
              <h4 className="info-card-title">{t("Material Information")}</h4>
            </div>
            <div className="info-card-content">
              <div className="field-row-group">
                <div className="field-row">
                  <label className="field-label" htmlFor="material">
                    {t("Type")}
                  </label>
                  <MaterialSelect
                    value={formData.material}
                    onChange={(val) => handleChange("material", val)}
                    options={materialTypes}
                    disabled={isSubmitting}
                    hasError={!!errors.material}
                  />
                  {errors.material && <span className="field-error">{errors.material}</span>}
                </div>

                <div className="field-row">
                  <label className="field-label" htmlFor="item_name">
                    {t("Item Name")}
                  </label>
                  <input
                    id="item_name"
                    type="text"
                    className="field-input"
                    value={formData.item_name}
                    onChange={(e) => handleChange("item_name", e.target.value)}
                    disabled={isSubmitting}
                    placeholder={t("Optional")}
                    maxLength={200}
                  />
                </div>
              </div>

              <div className="field-row">
                <label className="field-label" htmlFor="quantity">
                  {t("Quantity")}
                </label>
                <input
                  id="quantity"
                  type="number"
                  className={`field-input no-spinners ${errors.quantity ? "error" : ""}`}
                  value={formData.quantity}
                  onChange={(e) => handleChange("quantity", e.target.value)}
                  disabled={isSubmitting}
                  placeholder="0"
                  min="0"
                  step="0.01"
                />
                {errors.quantity && <span className="field-error">{errors.quantity}</span>}
              </div>
            </div>
          </div>

          {/* Pricing Card */}
          <div className="info-card">
            <div className="info-card-header">
              <div className="info-card-icon green">
                <FontAwesomeIcon icon={faDollarSign} />
              </div>
              <h4 className="info-card-title">{t("Pricing")}</h4>
            </div>
            <div className="info-card-content">
              <div className="field-row">
                <label className="field-label" htmlFor="cost">
                  {t("Cost per unit ($/lb)")}
                </label>
                <input
                  id="cost"
                  type="number"
                  className={`field-input no-spinners ${errors.cost ? "error" : ""}`}
                  value={formData.cost}
                  onChange={(e) => handleChange("cost", e.target.value)}
                  disabled={isSubmitting}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
                {errors.cost && <span className="field-error">{errors.cost}</span>}
              </div>
              <div className="field-row">
                <label className="field-label" htmlFor="acquisition_cost">
                  {t("Original cost per unit ($/lb)")}
                  <span className="field-label-hint">{t("helps calculate value recovery")}</span>
                </label>
                <input
                  id="acquisition_cost"
                  type="number"
                  className={`field-input no-spinners ${errors.acquisition_cost ? "error" : ""}`}
                  value={formData.acquisition_cost}
                  onChange={(e) => handleChange("acquisition_cost", e.target.value)}
                  disabled={isSubmitting}
                  placeholder={t("Optional")}
                  min="0"
                  step="0.01"
                />
                {errors.acquisition_cost && <span className="field-error">{errors.acquisition_cost}</span>}
              </div>
            </div>
          </div>

          {/* Deadline & Instructions Card */}
          <div className="info-card">
            <div className="info-card-header">
              <div className="info-card-icon orange">
                <FontAwesomeIcon icon={faTruck} />
              </div>
              <h4 className="info-card-title">{t("Logistics")}</h4>
            </div>
            <div className="info-card-content">
              <div className="field-row">
                <label className="field-label" htmlFor="latest_pickup">
                  {t("Pickup Deadline")}
                </label>
                <input
                  id="latest_pickup"
                  type="date"
                  className={`field-input ${errors.latest_pickup ? "error" : isDuplicate && !formData.latest_pickup ? "needs-attention" : ""}`}
                  value={formData.latest_pickup}
                  onChange={(e) => handleChange("latest_pickup", e.target.value)}
                  disabled={isSubmitting}
                  min={today}
                />
                {errors.latest_pickup && (
                  <span className="field-error">{errors.latest_pickup}</span>
                )}
              </div>

              <div className="field-row">
                <label className="field-label" htmlFor="expiry_disposition">
                  {t("If unsold by deadline")}
                </label>
                <select
                  id="expiry_disposition"
                  className="field-input"
                  value={formData.expiry_disposition}
                  onChange={(e) => handleChange("expiry_disposition", e.target.value)}
                  disabled={isSubmitting}
                >
                  <option value="">{t("Not specified")}</option>
                  <option value="landfill">{t("Landfill")}</option>
                  <option value="incineration">{t("Incineration")}</option>
                  <option value="recycling">{t("Recycling")}</option>
                  <option value="relisted">{t("Re-list on Punar")}</option>
                  <option value="unknown">{t("Unknown")}</option>
                </select>
              </div>

              <div className="field-row">
                <label className="field-label" htmlFor="delivery_method">
                  {t("Delivery Method")}
                </label>
                <select
                  id="delivery_method"
                  className="field-input"
                  value={formData.delivery_method || "pickup"}
                  onChange={(e) => {
                    const method = e.target.value;
                    handleChange("delivery_method", method);

                    if (method !== "delivery") {
                      handleChange("transport_mode", "");
                    }
                  }}
                  disabled={isSubmitting}
                >
                  <option value="pickup">{t("Pickup")}</option>
                  <option value="delivery">{t("Delivery")}</option>
                </select>
              </div>
              {(formData.delivery_method || "pickup") === "delivery" && (
                <div className="field-row">
                  <label className="field-label" htmlFor="transport_mode">
                    {t("Mode of Transportation")}
                  </label>
                  <input
                    id="transport_mode"
                    type="text"
                    className={`field-input ${errors.transport_mode ? "error" : ""}`}
                    value={formData.transport_mode || ""}
                    onChange={(e) => handleChange("transport_mode", e.target.value)}
                    placeholder={t("e.g., Car, Truck, Bike...")}
                    disabled={isSubmitting}
                    maxLength={100}
                  />
                  {errors.transport_mode && (
                    <span className="field-error">{errors.transport_mode}</span>
                  )}
                </div>
              )}

              {/* Instructions */}
              <div className="field-row">
                <label className="field-label" htmlFor="delivery_instructions">
                  {t("Instructions")}
                </label>
                <textarea
                  id="delivery_instructions"
                  className={`field-input field-textarea ${
                    errors.delivery_instructions ? "error" : ""
                  }`}
                  value={formData.delivery_instructions}
                  onChange={(e) => handleChange("delivery_instructions", e.target.value)}
                  placeholder={
                    (formData.delivery_method || "pickup") === "delivery"
                      ? t("Special instructions for delivery")
                      : t("Special instructions for pickup")
                  }
                  rows="5"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="panel-column">
          {/* Location Card */}
          <div className="info-card">
            <div className="info-card-header">
              <div className="info-card-icon red">
                <FontAwesomeIcon icon={faMapMarkerAlt} />
              </div>
              <h4 className="info-card-title">{t("Pickup Location")}</h4>
            </div>
            <div className="info-card-content">
              <div className="field-row">
                <label className="field-label" htmlFor="address">
                  {t("Address")}
                </label>
                <input
                  id="address"
                  type="text"
                  className={`field-input ${
                    formData.use_default_address ? "disabled" : ""
                  } ${errors.address ? "error" : ""}`}
                  value={formData.address}
                  onChange={(e) => handleChange("address", e.target.value)}
                  placeholder={t("Enter address")}
                  disabled={isSubmitting || formData.use_default_address}
                />
              </div>
              <div className="field-row">
                <label className="field-label" htmlFor="city">
                  {t("City")}
                </label>
                <input
                  id="city"
                  type="text"
                  className={`field-input ${
                    formData.use_default_address ? "disabled" : ""
                  } ${errors.city ? "error" : ""}`}
                  value={formData.city}
                  onChange={(e) => handleChange("city", e.target.value)}
                  placeholder={t("Enter city")}
                  disabled={isSubmitting || formData.use_default_address}
                />
              </div>
              <label className="toggle-row">
                <input
                  type="checkbox"
                  checked={formData.use_default_address}
                  onChange={(e) => handleChange("use_default_address", e.target.checked)}
                  disabled={isSubmitting}
                />
                <span className="toggle-label">{t("Use default address")}</span>
              </label>
            </div>
          </div>

          {/* Contact Card */}
          <div className="info-card">
            <div className="info-card-header">
              <div className="info-card-icon purple">
                <FontAwesomeIcon icon={faEnvelope} />
              </div>
              <h4 className="info-card-title">{t("Contact Information")}</h4>
            </div>
            <div className="info-card-content">
              <div className="field-row">
                <label className="field-label" htmlFor="contact_email">
                  {t("Email")}
                </label>
                <input
                  id="contact_email"
                  type="email"
                  className={`field-input ${
                    !formData.share_contact ? "disabled" : ""
                  } ${errors.contact_email ? "error" : ""}`}
                  value={formData.contact_email}
                  onChange={(e) => handleChange("contact_email", e.target.value)}
                  placeholder={t("Enter email")}
                  disabled={isSubmitting || !formData.share_contact}
                />
              </div>
              <div className="field-row">
                <label className="field-label" htmlFor="contact_phone">
                  {t("Phone")}
                </label>
                <input
                  id="contact_phone"
                  type="tel"
                  className={`field-input ${
                    !formData.share_contact ? "disabled" : ""
                  } ${errors.contact_phone ? "error" : ""}`}
                  value={formData.contact_phone}
                  onChange={(e) => handleChange("contact_phone", e.target.value)}
                  placeholder={t("Enter phone")}
                  disabled={isSubmitting || !formData.share_contact}
                />
              </div>
              <label className="toggle-row">
                <input
                  type="checkbox"
                  checked={formData.share_contact}
                  onChange={(e) => handleChange("share_contact", e.target.checked)}
                  disabled={isSubmitting}
                />
                <span className="toggle-label">{t("Share contact on listing")}</span>
              </label>
            </div>
          </div>

          {/* Pickup Schedule Card */}
          <div className="info-card">
            <div className="info-card-header">
              <div className="info-card-icon blue">
                <FontAwesomeIcon icon={faClock} />
              </div>
              <h4 className="info-card-title">{t("Pickup Schedule")}</h4>
            </div>
            <div className="info-card-content">
              <div className="field-row-group">
                <div className="field-row">
                  <label className="field-label" htmlFor="pickup_time_start">
                    {t("From")}
                  </label>
                  <input
                    id="pickup_time_start"
                    type="time"
                    className={`field-input ${errors.pickup_time_start ? "error" : ""}`}
                    value={formData.pickup_time_start}
                    onChange={(e) => handleChange("pickup_time_start", e.target.value)}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="field-row">
                  <label className="field-label" htmlFor="pickup_time_end">
                    {t("To")}
                  </label>
                  <input
                    id="pickup_time_end"
                    type="time"
                    className={`field-input ${errors.pickup_time_end ? "error" : ""}`}
                    value={formData.pickup_time_end}
                    onChange={(e) => handleChange("pickup_time_end", e.target.value)}
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div className="field-row">
                <label className="field-label">{t("Days")}</label>
                <div className={`days-row ${errors.available_days ? "error" : ""}`}>
                  {daysConfig.map((day) => (
                    <button
                      key={day.key}
                      type="button"
                      className={`day-chip ${formData.available_days[day.key] ? "active" : ""}`}
                      onClick={() => handleDayToggle(day.key)}
                      disabled={isSubmitting}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
                {errors.available_days && (
                  <span className="field-error">{errors.available_days}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="panel-actions">
        <ActionButton onClick={handleClose} variant="default" disabled={isSubmitting}>
          {t("Cancel")}
        </ActionButton>
        <ActionButton
          onClick={handleSubmit}
          variant="secondary"
          loading={isSubmitting}
          icon={faCheck}
        >
          {t("Create Listing")}
        </ActionButton>
      </div>
    </Card>
  );
};

AddListingPanel.propTypes = {
  darkMode: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  listingsData: PropTypes.array,
  initialData: PropTypes.object,
};

export default AddListingPanel;
